/**
 * VÉRAULT — Public preview route
 *
 * /preview/:slug?mode=draft|published
 *
 * Renders a page from CMS blocks with a sticky DRAFT PREVIEW banner along
 * the top. Used both by the editor "Preview draft" button and the live
 * "View live preview" link on each Website-Editor page card.
 *
 * Draft mode requires advisor auth (the backend gates it). Published mode
 * is public.
 */
import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, Eye } from "lucide-react";
import axios from "axios";
import SiteBlocksRenderer from "../portal/components/SiteBlocksRenderer";
import PlanPage from "./public/PlanPage";
import DestinationsPage from "./public/DestinationsPage";
import BlogPage from "./public/BlogPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEDIA_BASE = `${API}/cms/media/file`;
const api = axios.create({ baseURL: API, withCredentials: true });

const SLOT_OVERRIDES = {
  inquiry_form: <PlanPage />,
  destinations_grid: <DestinationsPage />,
  journal_grid: <BlogPage />,
};

export default function PreviewPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const mode = (params.get("mode") || "draft").toLowerCase();
  const [page, setPage] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    const url = mode === "published"
      ? `/site-cms/public/${slug}`
      : `/site-cms/public/${slug}/draft`;
    api
      .get(url)
      .then(({ data }) => alive && setPage(data))
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          setError("This draft can only be previewed by signed-in advisors.");
        } else if (e?.response?.status === 404) {
          setError(mode === "published" ? "This page hasn't been published yet." : "Page not found.");
        } else {
          setError("Couldn't load the preview.");
        }
      });
    return () => { alive = false; };
  }, [slug, mode]);

  return (
    <div data-testid={`preview-${slug}`}>
      {/* Sticky preview banner — never gets in the way of the actual page */}
      <div className="sticky top-0 z-50 bg-[#1A1A1A] text-white py-2.5 px-4 flex items-center justify-between gap-3" data-testid="preview-banner">
        <div className="flex items-center gap-3 min-w-0">
          <Eye className="size-3.5 shrink-0" strokeWidth={1.5} />
          <p className="font-mono text-[10px] uppercase tracking-[0.22em]">
            {mode === "published" ? "Published preview" : "Draft preview"}
          </p>
          <p className="font-accent italic text-[12.5px] text-white/70 truncate hidden sm:block">
            /{slug} · branding is locked · published changes go live immediately
          </p>
        </div>
        <Link
          to={`/portal/advisor/content/website/${slug}`}
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80 hover:text-white"
          data-testid="preview-back-to-editor"
        >
          <ChevronLeft className="size-3" strokeWidth={1.5} />
          Back to editor
        </Link>
      </div>

      {error ? (
        <div className="py-32 px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#999]">Preview unavailable</p>
          <p className="mt-4 font-serif text-2xl text-[#1A1A1A]">{error}</p>
        </div>
      ) : !page ? (
        <div className="py-32 px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#999]">Loading the page…</p>
        </div>
      ) : (
        <SiteBlocksRenderer
          blocks={page.blocks}
          api={api}
          mediaBase={MEDIA_BASE}
          slotOverrides={SLOT_OVERRIDES}
        />
      )}
    </div>
  );
}
